import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronDown, 
  faChevronUp, 
  faPlaneArrival, 
  faHotel, 
  faPassport,
  faBus,
  faFileSignature,
  faTrophy,
  faMoneyBillWave,
  faCalendarCheck,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";

// Accordion Item styled to look like integrated cards
const AccordionItem = ({ title, icon, isOpen, onClick, children }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      <button
        className={`w-full px-4 py-4 text-left flex justify-between items-center transition-colors duration-300 ${
          isOpen ? "bg-gray-50" : "bg-white hover:bg-gray-50"
        }`}
        onClick={onClick}
      >
        <div className="flex items-center gap-3">
          <div className={`rounded-full flex items-center justify-center transition-colors text-sm ${
             isOpen ? "bg-gray-100 text-gray-500" : "bg-gray-100 text-gray-500"
          }`}>
             <FontAwesomeIcon icon={icon} />
          </div>
          <span className={`font-semibold text-md ${isOpen ? "text-gray-800" : "text-gray-800"}`}>
            {title}
          </span>
        </div>
        <FontAwesomeIcon 
          icon={isOpen ? faChevronUp : faChevronDown} 
          className={`transition-transform duration-300 text-sm ${isOpen ? "text-gray-800" : "text-gray-400"}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-4 py-8 bg-white border-t border-gray-100 text-gray-800">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const SectionInformation = () => {
  const [openAccordionIndex, setOpenAccordionIndex] = useState(0);

  const toggleAccordion = (index) => {
    setOpenAccordionIndex(openAccordionIndex === index ? -1 : index);
  };

  const visaCountries = [
    "Vietnam", "Brunei Darussalam", "Cambodia", "Laos", "Malaysia", "Myanmar", "Philippines", "Singapore", "Thailand"
  ];

  const quotaData = [
    { id: 1, event: "Long Trail 75K", gender: "Male", quota: 3, nonQuota: 3 },
    { id: 2, event: "", gender: "Female", quota: 3, nonQuota: 3 },
    { id: 3, event: "Short Trail 42K", gender: "Male", quota: 3, nonQuota: 3 },
    { id: 4, event: "", gender: "Female", quota: 3, nonQuota: 3 },
    { id: 5, event: "Classic 10K Senior", gender: "Male", quota: 3, nonQuota: 3 },
    { id: 6, event: "Senior", gender: "Female", quota: 3, nonQuota: 3 },
    { id: 7, event: "Classic 10K", gender: "Male", quota: 3, nonQuota: 3 },
    { id: 8, event: "Junior 15-22 y.o", gender: "Female", quota: 3, nonQuota: 3 },
    { id: 9, event: "Uphill 5K", gender: "Male", quota: 3, nonQuota: 3 },
    { id: 10, event: "", gender: "Female", quota: 3, nonQuota: 3 },
  ];

  const officialsQuotaData = [
    { range: "1 / 5 quota athletes", officials: 1 },
    { range: "6 / 10 quota athletes", officials: 2 },
    { range: "11 / 15 quota athletes", officials: 3 },
    { range: "16 + quota athletes", officials: 4 },
  ];

  const prizeMoneyData = [
    { category: "Long Trail", m1: "7.000", m2: "5.000", m3: "4.000", f1: "7.000", f2: "5.000", f3: "4.000" },
    { category: "Short Trail", m1: "4.000", m2: "3.000", m3: "2.000", f1: "4.000", f2: "3.000", f3: "2.000" },
    { category: "Mountain Classic Senior", m1: "3.500", m2: "2.500", m3: "1.500", f1: "3.500", f2: "2.500", f3: "1.500" },
    { category: "Mountain Classic Junior", m1: "3.500", m2: "2.500", m3: "1.500", f1: "3.500", f2: "2.500", f3: "1.500" },
    { category: "Mountain Uphill", m1: "2.500", m2: "1.500", m3: "1.000", f1: "2.500", f2: "1.500", f3: "1.000" },
  ];

  const awardsSchedule = [
    { what: "Medal Ceremony Uphill 5km Male", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Uphill 5km Female", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Classic Senior 10km Male", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Classic Senior 10km Female", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Classic Junior 10km Male", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Classic Junior 10km Female", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Short 42km Male", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Short 42km Female", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Long 75km Male", when: "26 Jul 2026", where: "Race Central" },
    { what: "Medal Ceremony Long 75km Female", when: "26 Jul 2026", where: "Race Central" },
  ];
  
  const ceremonyData = [
    { activity: "Opening Ceremony", where: "Race Central", when: "Friday, 24 July 2026", time: "16.00" },
    { activity: "Closing Ceremony", where: "Race Central", when: "Sunday, 26 July 2026", time: "10.00" },
  ];

  return (
    <section id="information" className="w-full py-20 text-center text-red-900 flex-col justify-center relative bg-white">
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4 text-left">
            <div className="text-center">
              <motion.h3
                className="text-3xl md:text-5xl mb-12 font-black text-gray-800 mt-2 uppercase"
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              >
                General Information
              </motion.h3>
            </div>

            {/* 1. Entry Visa */}
            <AccordionItem 
              title="Entry Visa" 
              icon={faPassport}
              isOpen={openAccordionIndex === 0} 
              onClick={() => toggleAccordion(0)}
            >
              <div className="space-y-6">
                
                {/* Visa Table - Integrated Style */}
                <div className="overflow-x-auto -mx-8 border-t border-b border-gray-100 mb-4">
                  <table className="w-full text-center divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="pl-8 pr-4 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider">Country</th>
                        <th className="px-4 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider">Visa</th>
                        <th className="pl-4 pr-8 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider">Visa On Arrival</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {visaCountries.map((country, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50">
                          <td className="pl-8 pr-4 py-3 whitespace-nowrap text-sm font-semi text-gray-600">{country}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">No</td>
                          <td className="pl-4 pr-8 py-3 text-sm text-gray-600">No</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-red-50 border border-red-100 p-4 rounded-lg text-red-900 text-center text-sm font-medium ">
                  All participants, passport holders originating from Southeast Asian countries are <strong>free from a visa</strong>.
                </div>

                <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>
                    If you have any doubts, you can consult the website of the Indonesian Immigration, where you can find updated information in the following links:
                  </p>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://evisa.imigrasi.go.id/front/info/evoa" target="_blank" rel="noopener noreferrer" className="font-medium text-red-700 hover:text-red-900 hover:underline break-all transition-colors">
                        https://evisa.imigrasi.go.id/front/info/evoa
                      </a>
                    </li>
                    <li>
                      <a href="https://evisa.imigrasi.go.id/" target="_blank" rel="noopener noreferrer" className="font-medium text-red-700 hover:text-red-900 hover:underline break-all transition-colors">
                        https://evisa.imigrasi.go.id/
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="text-gray-600">
                    Participants who need a visa to enter the country should contact the LOC via the following email:
                  </p>
                  <a href="mailto:admin@cakrawalajakarta.id" className="block text-lg font-bold text-gray-800 hover:text-red-900 transition-colors">
                    admin@cakrawalajakarta.id
                  </a>
                  <p className="text-gray-600 mt-2">
                    so that we can send them a letter of invitation as soon as possible. For this we will need the following information:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-600 marker:text-gray-800">
                    <li>Full name (first name and surname as it appears in the passport)</li>
                    <li>Nationality</li>
                    <li>Date of birth</li>
                    <li>Passport number</li>
                    <li>Inscription ID</li>
                    <li>Role in the team (athlete or official)</li>
                    <li>Date of entry into and departure from Indonesia</li>
                  </ul>
                </div>
              </div>
            </AccordionItem>

            {/* 2. Transportation */}
            <AccordionItem 
              title="Transportation" 
              icon={faBus}
              isOpen={openAccordionIndex === 1} 
              onClick={() => toggleAccordion(1)}
            >
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-800 text-lg mb-2">Service Overview</h4>
                  <p>The transport service offered by the LOC includes transport in the following situations:</p>
                  <ul className="mt-2 space-y-1 list-disc pl-5">
                    <li><strong>Airport to Hotel Shuttle:</strong> Arranged to transport guests from Soekarno Hatta International Airport (Terminal 3) to their designated hotels upon arrival.</li>
                    <li><strong>Hotel to Venue Shuttle:</strong> Provided daily between official hotels and the event venue to ensure timely arrival for all scheduled activities.</li>
                    <li><strong>Hotel to Airport Shuttle:</strong> Arranged to transport guests from the hotel to the airport or designated hotel after the event.</li>
                  </ul>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
                    <strong className="block text-gray-800 mb-1">Meeting Point</strong>
                    <p className="text-gray-800">
                      The LOC will have one entry point located at <strong className="font-bold text-gray-800">Soekarno Hatta International Airport (Terminal 3)</strong>. All participants will be picked up here.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
                    <strong className="block text-gray-800 mb-1">Important Notes</strong>
                    <ul className="list-disc pl-5 text-gray-800 space-y-1">
                      <li>Shuttle services are <strong>complimentary</strong> for all registered participants.</li>
                      <li>Detailed schedule with timings and locations will be shared later.</li>
                      <li>LOC's crew will be available to guide guests.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </AccordionItem>

            {/* 3. Accommodation */}
            <AccordionItem 
              title="Accommodation" 
              icon={faHotel}
              isOpen={openAccordionIndex === 2} 
              onClick={() => toggleAccordion(2)}
            >
              <div className="space-y-8">
                <div className="space-y-4 text-gray-600 text-sm leading-relaxed text-justify">
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-gray-800">
                    <h4 className="font-bold text-lg mb-2">Price Guarantee</h4>
                    <p>The LOC guarantees the price of <strong>$100 per person per night</strong> (taxes included) during the event dates (24 July to 26 July), plus one extra night before (23 July) or after (27 July).</p>
                  </div>

                  <p>
                    Participating teams will be allocated to selected hotels in Cianjur. The teams will stay together in the same hotel (if possible). Hotel details will be circulated later.
                  </p>
                  
                  <div>
                    <h4 className="font-bold text-gray-800 text-base mb-2">Team Officials Support</h4>
                    <p className="mb-4">
                      As for the athletes, the LOC will cover the cost of 4 nights for each team official as outlined below. Payment for surplus nights is the responsibility of the Member Federation.
                    </p>
                    
                    {/* Officials Quota Table */}
                    <div className="overflow-x-auto -mx-8 border-t border-b mb-6">
                      <table className="w-full text-center ">
                        <thead className="bg-gray-200">
                          <tr>
                            <th className="pl-8 pr-4 py-3 text-sm font-black text-gray-800 uppercase">Number of Athletes</th>
                            <th className="pl-4 pr-8 py-3 text-sm font-black text-gray-800 uppercase">Team Officials paid by LOC (4 nights)</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-sm">
                          {officialsQuotaData.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="pl-8 pr-4 py-3 whitespace-nowrap font-medium text-gray-800">{row.range}</td>
                              <td className="pl-4 pr-8 py-3 whitespace-nowrap font-bold text-gray-800">{row.officials}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-base mb-2">Athlete Accommodation Quota</h4>
                    <p className="mb-4">
                      The LOC will pay full board accommodation for a maximum of athletes as set out in the table below for 4 nights. There will be 2 athletes per room.
                    </p>
                    
                    {/* Athletes Quota Table */}
                    <div className="overflow-x-auto -mx-8">
                      <table className="w-full text-center">
                        <thead className="bg-gray-200">
                          <tr>
                            <th className="pl-8 pr-4 py-3 border-r border-gray-100 text-sm font-bold text-gray-800 uppercase tracking-wider">No</th>
                            <th className="px-4 py-3 border-r border-gray-100 text-sm font-bold text-gray-800 uppercase tracking-wider">Event</th>
                            <th className="px-4 py-3 border-r border-gray-100 text-sm font-bold text-gray-800 uppercase tracking-wider">Gender</th>
                            <th className="px-4 py-3 border-r border-gray-100 text-sm font-bold text-gray-800 uppercase tracking-wider">Quota</th>
                            <th className="pl-4 pr-8 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider">Non-Quota</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100 text-sm">
                          {quotaData.map((row, idx) => (
                            <tr key={row.id} className="hover:bg-gray-50/50">
                              <td className="pl-8 pr-4 py-2 border-r border-gray-100 font-medium text-gray-600">{row.id}</td>
                              <td className="px-4 py-2 border-r border-gray-100 font-bold text-gray-800">{row.event}</td>
                              <td className="px-4 py-2 border-r border-gray-100 text-gray-600">{row.gender}</td>
                              <td className="px-4 py-2 border-r border-gray-100 font-bold text-gray-800">{row.quota}</td>
                              <td className="pl-4 pr-8 py-2 font-bold text-gray-800">{row.nonQuota}</td>
                            </tr>
                          ))}
                          <tr className="bg-gray-200 font-bold text-base">
                            <td colSpan={3} className="pl-8 pr-4 py-3 text-right border-r border-gray-200 text-gray-800">Total</td>
                            <td className="px-4 py-3 border-r border-gray-200 text-gray-800">30</td>
                            <td className="pl-4 pr-8 py-3 text-gray-800">30</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionItem>

            {/* 4. Entry Forms */}
            <AccordionItem 
              title="Preliminary / Final Entry Forms" 
              icon={faFileSignature}
              isOpen={openAccordionIndex === 3} 
              onClick={() => toggleAccordion(3)}
            >
              <div className="space-y-4 text-gray-600">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center">
                    <h4 className="text-gray-500 text-sm font-bold uppercase tracking-wider mb-2">Preliminary Entry Deadline</h4>
                    <p className="text-2xl font-black text-gray-800">March 31st, 2026</p>
                    <p className="text-sm mt-2 text-gray-500">Entered into Entry System</p>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center">
                    <h4 className="text-gray-500 text-sm font-bold uppercase tracking-wider mb-2">Final Entry Deadline</h4>
                    <p className="text-2xl font-black text-gray-800">May 20th, 2026</p>
                    <p className="text-sm mt-2 text-gray-500">Entered into Entry System</p>
                  </div>
                </div>
              </div>
            </AccordionItem>

            {/* 5. Championship Categories */}
            <AccordionItem 
              title="Championship Category" 
              icon={faTrophy}
              isOpen={openAccordionIndex === 4} 
              onClick={() => toggleAccordion(4)}
            >
              <div className="space-y-6 text-gray-600 text-sm leading-relaxed">
                <p>All the races have the following categories:</p>
                <ul className="list-disc pl-5 font-semibold text-gray-800 space-y-1">
                  <li>Male's Individual</li>
                  <li>Female's Individual</li>
                </ul>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-lg text-gray-800 mb-1">Overall Team Trophy</h4>
                  <p> In addition to individual team championships for each race, an overall Team Trophy will be awarded to the Federation that scores highest from a number of the senior races both mountain and trail.</p>
                </div>
              </div>
            </AccordionItem>

            {/* 6. Prize Money & Awards (Hidden) */}

            {/* 7. Opening & Closing Ceremony */}
            <AccordionItem 
              title="Opening And Closing Ceremony" 
              icon={faCalendarCheck}
              isOpen={openAccordionIndex === 6} 
              onClick={() => toggleAccordion(6)}
            >
              <div className="space-y-6">
                <div className="overflow-x-auto -mx-8 border-t border-b border-gray-100">
                  <table className="w-full text-center divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="pl-8 pr-4 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider text-left border-r border-gray-200">Activity</th>
                        <th className="px-4 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider border-r border-gray-200">Where</th>
                        <th className="px-4 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider border-r border-gray-200">When</th>
                        <th className="pl-4 pr-8 py-3 text-sm font-bold text-gray-800 uppercase tracking-wider">Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 text-sm">
                      {ceremonyData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50">
                          <td className="pl-8 pr-4 py-3 font-semibold text-gray-800 text-left border-r border-gray-100">{row.activity}</td>
                          <td className="px-4 py-3 text-gray-800 border-r border-gray-100">{row.where}</td>
                          <td className="px-4 py-3 text-gray-800 border-r border-gray-100">{row.when}</td>
                          <td className="pl-4 pr-8 py-3 font-bold text-gray-800">{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </AccordionItem>
            
            {/* 8. Insurance And Indemnities */}
            <AccordionItem 
              title="Insurance And Indemnities" 
              icon={faShieldAlt}
              isOpen={openAccordionIndex === 7} 
              onClick={() => toggleAccordion(7)}
            >
              <div className="space-y-6 text-gray-600 text-sm leading-relaxed text-justify">
                <p>
                  Participating Member Federations are responsible for taking out their own insurance to cover the risk of illness or injury of any member of their team when travelling to and from the origin country and during the event itself. Please take the necessary steps to fulfil these requirements well in advance.
                </p>
                <p>
                   Except in relation to gross negligence or wilful misconduct of the organiser (LOC), the Participating Member Federations defend and hold harmless the organiser from and against any personal injury or material damages, losses, and claims arising out of the participation at the Championships.
                </p>
               
              </div>
            </AccordionItem>

          </div>
        </div>
      </div>
    </section>
  );
};
